import { useState, useCallback, useEffect, lazy, Suspense } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useOfflineShoppingLists } from '@/hooks/useOfflineShoppingLists';
import Header from "@/components/layout/Header";
import Sidebar from "@/components/layout/Sidebar";
import { Button } from "@/components/ui/button";
import { ShoppingList } from "@/types";
import { ListPlus } from "lucide-react";
const CreateListDialog = lazy(() => import("@/components/ui/CreateListDialog"));
import { useToast } from "@/hooks/use-toast";
import { useUndoToast } from "@/hooks/useUndoToast";
import BackgroundColorPicker from "@/components/shopping/lists/BackgroundColorPicker";
import ListCard from "@/components/shopping/lists/ListCard";
import { useThemeConfig } from "@/components/ui/theme-config-provider";
import { deleteShoppingList, createShoppingList, getShoppingList } from "@/services/shopping/lists.service";
import { setPrimaryList } from "@/services/shopping/lists/primary-list.service";
import { updateListBackground } from "@/services/shopping/lists/update-list.service";
import { addShoppingItem, updateItemStatus } from "@/services/shopping/items/index";
import { validateQuantity } from "@/utils/security";
import HelpButton from "@/components/tutorial/HelpButton";
import { useTutorialManager } from "@/hooks/useTutorialManager";
import { useLocation } from "react-router-dom";
import { shoppingListTutorial } from "@/components/tutorial/ShoppingListTutorial";
import TutorialSpotlight from "@/components/tutorial/TutorialSpotlight";
import { createEntityWithOfflineFallback } from '@/services/createEntityWithOfflineFallback';
import { useSmartAutoSync } from '@/hooks/useSmartAutoSync';
import { smartAutoSyncService } from '@/services/SmartAutoSyncService';
import { useSafeNavigation } from '@/hooks/useSafeNavigation';
import { useAuth } from "@/context/auth/AuthContext";
import { useResetModals } from '@/hooks/useResetModals';
import { trackListCreated } from '@/lib/analyticsEvents';

const ShoppingListsPage = () => {
  const { safeNavigate, isNavigating } = useSafeNavigation();
  const { toast } = useToast();
  const undoToast = useUndoToast();
  const { theme } = useThemeConfig();
  const queryClient = useQueryClient();
  const [isCreatingList, setIsCreatingList] = useState(false);
  const [listToChangeBackground, setListToChangeBackground] = useState<ShoppingList | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useAuth();
  useResetModals();

  const location = useLocation();
  const path = location.pathname;
  const pageId = `tutorial${path.replace(/\//g, '-')}`;
  const { isOpen, openTutorial, closeTutorial } = useTutorialManager({
    pageId,
    showOnFirstVisit: true
  });

  // ✅ FIX: Aggiunge auto-sync intelligente all'ingresso della pagina
  const handleSyncComplete = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['shopping-lists'] });
  }, [queryClient]);

  const { manualSync } = useSmartAutoSync({
    entities: ['shoppingLists', 'shoppingItems'],
    onSyncComplete: handleSyncComplete,
    enabled: true
  });

  useEffect(() => {
    smartAutoSyncService.forceSync('page-entry', ['shoppingLists', 'shoppingItems']);
  }, []);

  const handleMenuClick = () => {
    setSidebarOpen(true);
  };

  useEffect(() => {
    if (!listToChangeBackground) {
      document.body.style.pointerEvents = 'auto';
    }
  }, [listToChangeBackground]);

  const { shoppingLists, isLoading, error } = useOfflineShoppingLists();
  
  const handleCreateList = async (name: string): Promise<void> => {
    const result = await createEntityWithOfflineFallback<ShoppingList>({
      entityKey: 'shoppingLists',
      data: { name },
      supabaseCreateFn: async (list) => {
        const { createShoppingList } = await import('@/services/shopping/lists/create-list.service');
        await createShoppingList(list.name);
      },
      toastSuccessMessage: "Lista creata con successo",
      toastErrorMessage: "Impossibile creare la lista",
      onAfterCreate: () => {
        queryClient.invalidateQueries({ queryKey: ['shopping-lists'] });
        setIsCreatingList(false);
      }
    });

    if (result.success) {
      trackListCreated();
      toast({ description: result.message, variant: 'success' });
    } else {
      toast({ title: "Errore", description: result.message, variant: 'destructive' });
    }
  };
  
  const handleDeleteList = async (listId: string) => {
    try {
      const detailedList = await getShoppingList(listId);
      const removedList = detailedList || shoppingLists?.find(l => l.id === listId);
      const removedItems = detailedList?.items || [];

      await deleteShoppingList(listId);
      await queryClient.invalidateQueries({ queryKey: ['shopping-lists'] });

      if (removedList) {
        undoToast(
          `delete-list-${listId}`,
          "Lista eliminata con successo",
          async () => {
            try {
              const newList = await createShoppingList(removedList.name);
              if (newList) {
                for (const item of removedItems) {
                  try {
                    const quantityValidation = validateQuantity(item.quantity);
                    const sanitizedQuantity = quantityValidation.isValid ? quantityValidation.sanitized : "1";

                    const recreated = await addShoppingItem(newList.id, {
                      product_id: item.product_id,
                      quantity: sanitizedQuantity,
                      unit: item.unit,
                      category: item.category,
                      position: item.position,
                      attributes: item.attributes,
                    });
                    if (item.checked && recreated?.id) {
                      await updateItemStatus(recreated.id, true);
                    }
                  } catch (err) {
                    console.error('Error recreating item:', err);
                    toast({
                      title: 'Errore',
                      description: 'Impossibile ripristinare un articolo',
                      variant: 'destructive',
                    });
                  }
                }
                queryClient.invalidateQueries({ queryKey: ['shopping-lists'] });
                queryClient.invalidateQueries({ queryKey: ['shopping-items', newList.id] });
                queryClient.invalidateQueries({ queryKey: ['primary-list'] });
              }
            } catch (err) {
              console.error('Error recreating list:', err);
              toast({
                title: 'Errore',
                description: 'Impossibile ripristinare la lista',
                variant: 'destructive',
              });
            }
          }
        );
      } else {
        toast({ description: 'Lista eliminata con successo', variant: 'success' });
      }
    } catch (err) {
      console.error('Error deleting list:', err);
      toast({
        title: 'Errore',
        description: 'Impossibile eliminare la lista',
        variant: 'destructive',
      });
    }
  };
  
  const handleSetAsPrimary = async (listId: string) => {
    try {
      const previousPrimary = shoppingLists?.find(l => l.isPrimary || l.is_primary);
      await setPrimaryList(listId);
      await queryClient.invalidateQueries({ queryKey: ['shopping-lists'] });
      await queryClient.invalidateQueries({ queryKey: ['primary-list'] });

      undoToast(
        `set-primary-${listId}`,
        "Lista impostata come principale",
        () => {
          if (previousPrimary) {
            setPrimaryList(previousPrimary.id)
              .then(() => {
                queryClient.invalidateQueries({ queryKey: ['shopping-lists'] });
                queryClient.invalidateQueries({ queryKey: ['primary-list'] });
              })
              .catch(err => console.error("Error restoring primary list:", err));
          }
        }
      );
    } catch (err) {
      console.error("Error setting primary list:", err);
      toast({
        title: "Errore",
        description: "Impossibile impostare la lista come principale",
        variant: "destructive",
      });
    }
  };

  const handleBackgroundChange = (list: ShoppingList) => {
    setListToChangeBackground(list);
  };
  
  const handleBackgroundDialogClose = () => {
    setListToChangeBackground(null);
  };

  // ✅ FIX: Wrapper per navigazione sicura verso le liste
  const handleOpenList = async (listId: string, mode: 'prepare' | 'shop') => {
    const success = await safeNavigate(`/shopping?list=${listId}&mode=${mode}`, {
      validateBeforeNav: () => {
        // Verifica che la lista esista
        const listExists = shoppingLists?.some(list => list.id === listId);
        if (!listExists) {
          toast({
            title: "Errore",
            description: "Lista non trovata",
            variant: "destructive"
          });
          return false;
        }
        return true;
      },
      timeout: 3000
    });

    if (!success) {
      toast({
        title: "Errore",
        description: "Impossibile aprire la lista",
        variant: "destructive"
      });
    }
  };

  const typedLists = shoppingLists as ShoppingList[] | undefined;

  return (
    <div
      className="min-viewport-height"
      style={{
        backgroundColor: theme.backgroundColor,
        color: theme.textColor,
        fontFamily: theme.fontFamily,
      }}
    >
      <Header 
        onMenuClick={() => setSidebarOpen(true)} 
        onAddClick={() => setIsCreatingList(true)} 
      />
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <main className="container max-w-md px-4 pb-20 pt-4">
        <div className="flex justify-between items-center mb-6">
          <h1
            className="text-2xl font-bold"
            style={{ color: theme.sectionTitleColor }}
          >
            Le tue liste
          </h1>
          <HelpButton onClick={openTutorial} />
        </div>

        {isLoading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="h-32 animate-pulse"
                style={{
                  backgroundColor: theme.cardBackgroundColor,
                  borderRadius: theme.cardBorderRadius,
                  boxShadow: theme.cardShadow,
                }}
              ></div>
            ))}
          </div>
        ) : error ? (
          <div
            className="text-center p-8"
            style={{
              backgroundColor: theme.cardBackgroundColor,
              borderRadius: theme.cardBorderRadius,
              boxShadow: theme.cardShadow,
            }}
          >
            <p style={{ color: theme.errorColor }}>Errore nel caricamento delle liste</p>
            <Button 
              onClick={manualSync}
              className="mt-2"
              variant="outline"
            >
              Riprova sincronizzazione
            </Button>
          </div>
        ) : typedLists && typedLists.length > 0 ? (
          <div className="grid gap-4">
            {typedLists.map((list) => (
              <ListCard 
                key={list.id}
                list={list}
                onDelete={handleDeleteList}
                onSetPrimary={handleSetAsPrimary}
                onChangeBackground={() => handleBackgroundChange(list)}
                onOpenList={handleOpenList}
                showSetPrimaryOption={!list.isPrimary}
                isNavigating={isNavigating}
              />
            ))}
          </div>
        ) : (
          <div
            className="text-center p-8"
            style={{
              backgroundColor: theme.cardBackgroundColor,
              borderRadius: theme.cardBorderRadius,
              boxShadow: theme.cardShadow,
            }}
          >
            <p className="mb-4" style={{ color: theme.sectionTextColor }}>Non hai ancora nessuna lista</p>
            <Button
              onClick={() => setIsCreatingList(true)}
              style={{
                backgroundColor: theme.primaryColor,
                color: theme.textPrimaryColor,
                fontWeight: 'bold',
                borderRadius: theme.buttonBorderRadius,
                padding: theme.buttonPadding,
                transitionDuration: theme.transitionSpeed,
              }}
            >
              <ListPlus className="h-5 w-5 mr-2" />
              Crea la tua prima lista
            </Button>
          </div>
        )}
      </main>
      
      {isCreatingList && (
        <Suspense fallback={null}>
          <CreateListDialog
            open={isCreatingList}
            onOpenChange={setIsCreatingList}
            listCount={typedLists?.length || 0}
            onCreateList={handleCreateList}
          />
        </Suspense>
      )}
      
      {listToChangeBackground && (
        <BackgroundColorPicker
          listId={listToChangeBackground.id}
          isOpen={!!listToChangeBackground}
          onOpenChange={handleBackgroundDialogClose}
          currentColor={listToChangeBackground.backgroundGradient || ""}
        />
      )}

      <TutorialSpotlight
        steps={shoppingListTutorial}
        isOpen={isOpen}
        onClose={closeTutorial}
        pageId={pageId}
      />
    </div>
  );
};

export default ShoppingListsPage;
